import { computeSchedule } from "@/engine/cpm";
import { addDays, isISODate, toDay, type ISODate } from "@/engine/date";
import { formatDate } from "@/lib/date-format";
import type { Project } from "@/engine/types";
import { weekStartMonday, type LoadMark } from "./person-load";
import type { PersonWork } from "./directory";

export type MemberScenario = "default" | "empty" | "heavy" | "unbalanced";

export interface MemberWeeklyThroughput {
  weekStart: string;
  label: string;
  count: number;
  isOverTarget: boolean;
  isCurrent: boolean;
}

export interface MemberBurndownForecast {
  startDate: string;
  today: string;
  targetDate: string;
  totalScope: number;
  completedScope: number;
  progressPercent: number;
  paceRatio: number;
  forecastDate: string;
  daysMargin: number;
  isAhead: boolean;
  isOverdueRisk: boolean;
  actualCurve: Array<{ date: string; percent: number }>;
  weeklyThroughput: MemberWeeklyThroughput[];
  targetAllowancePerWeek: number;
}

export interface MemberActivityItem {
  id: string;
  date: string;
  type: "task_done" | "record_closed" | "comment" | "policy" | "in_progress" | "risk_mitigated";
  badge: string;
  title: string;
  projectName: string;
  projectId: string;
  detail?: string;
}

export interface MemberProjectStrip {
  projectId: string;
  projectName: string;
  projectRole: string;
  allocationRatio: number;
  progress: number;
  paceRatio: number;
  targetDate?: string;
  statusLabel: string;
  statusTone: "success" | "warn" | "neutral";
  weeks: Array<{
    weekStart: string;
    level: 0 | 1 | 2 | 3 | 4;
    count: number;
    isCurrent: boolean;
  }>;
}

export interface MemberHeatmapCell {
  key: string;
  date: string;
  value: number | null;
  tooltip: string;
  marked?: boolean;
}

export interface MemberHeatmapRow {
  key: string;
  label: string;
  cells: MemberHeatmapCell[];
}

export interface MemberActivityHeatmap {
  columns: Array<{ key: string; label: string }>;
  rows: MemberHeatmapRow[];
  peakColumn: number | null;
  totalActivities: number;
  busiestWeekdayLabel: string | null;
}

export interface MemberTaskDistribution {
  total: number;
  done: number;
  inProgress: number;
  overdue: number;
  notStarted: number;
}

export interface MemberProjectWorkloadItem {
  projectId: string;
  projectName: string;
  role: string;
  allocationRatio: number;
  plannedMM: number;
  openTasksCount: number;
  completedTasksCount: number;
}

export interface MemberAnalytics {
  burndown: MemberBurndownForecast;
  activityFeed: MemberActivityItem[];
  projectStrips: MemberProjectStrip[];
  activityHeatmap: MemberActivityHeatmap;
  taskDistribution: MemberTaskDistribution;
  projectWorkload: MemberProjectWorkloadItem[];
}

function parseDay(str?: string): ISODate | undefined {
  if (!str) return undefined;
  const day = str.slice(0, 10);
  return isISODate(day) ? (day as ISODate) : undefined;
}

export function computeMemberAnalytics(
  projects: Record<string, Project>,
  resourceId: string,
  name: string,
  today: ISODate,
): MemberAnalytics {
  const activeProjects = Object.values(projects).filter(
    (p) => p.lifecycle !== "archived" && p.lifecycle !== "trashed",
  );

  type RawItem = {
    id: string;
    projectId: string;
    projectName: string;
    title: string;
    kind: "schedule" | "execution" | "record";
    subKind?: string;
    done: boolean;
    date?: ISODate;
    dueDate?: ISODate;
    plannedMM?: number;
    percent?: number;
    summary?: string;
  };

  const items: RawItem[] = [];
  const activities: MemberActivityItem[] = [];

  for (const project of activeProjects) {
    const scheduled = new Map<string, { finish?: string; summary: boolean }>();
    try {
      for (const [id, task] of computeSchedule(project).tasks) {
        scheduled.set(id, { finish: task.finish, summary: task.isSummary });
      }
    } catch {
      // ignore schedule solve failure
    }
    const parents = new Set(project.tasks.flatMap((t) => (t.parentId ? [t.parentId] : [])));

    // Schedule tasks
    for (const task of project.tasks) {
      if (task.assignedResourceId !== resourceId && task.assignee !== name) continue;
      const isParent = scheduled.get(task.id)?.summary ?? parents.has(task.id);
      if (isParent) continue;

      const done = (task.percentComplete ?? 0) >= 100;
      const finish = parseDay(scheduled.get(task.id)?.finish) ?? parseDay(task.deadline);
      const plannedMM = task.plannedMM ?? 0;

      items.push({
        id: task.id,
        projectId: project.id,
        projectName: project.name,
        title: task.name,
        kind: "schedule",
        done,
        date: finish,
        dueDate: parseDay(task.deadline) ?? finish,
        plannedMM,
        percent: task.percentComplete ?? 0,
      });

      if (done && finish) {
        activities.push({
          id: `act-sched-${task.id}`,
          date: finish,
          type: "task_done",
          badge: "일정 완료",
          title: task.name,
          projectName: project.name,
          projectId: project.id,
          detail: plannedMM > 0 ? `공수 ${plannedMM} M/M 달성` : undefined,
        });
      }
    }

    // Execution tasks
    for (const task of project.executionTasks ?? []) {
      const isAssigned = (task.assignees ?? []).includes(name) || (task.assignees ?? []).includes(resourceId);
      const done = task.status === "done";
      const completedDate = parseDay(task.completedAt);
      const dueDate = parseDay(task.due);

      if (isAssigned) {
        items.push({
          id: task.id,
          projectId: project.id,
          projectName: project.name,
          title: task.title,
          kind: "execution",
          done,
          date: done ? (completedDate ?? dueDate) : dueDate,
          dueDate,
          summary: task.notes,
        });

        if (done && (completedDate || dueDate)) {
          activities.push({
            id: `act-exec-${task.id}`,
            date: completedDate ?? dueDate!,
            type: "task_done",
            badge: "작업 완료",
            title: task.title,
            projectName: project.name,
            projectId: project.id,
            detail: task.notes,
          });
        }
      }

      for (const comment of task.comments ?? []) {
        if (comment.author === name && comment.at) {
          const cDate = parseDay(comment.at);
          if (cDate) {
            activities.push({
              id: `act-cm-${comment.id}`,
              date: cDate,
              type: "comment",
              badge: "검토 의견",
              title: `${task.title}`,
              projectName: project.name,
              projectId: project.id,
              detail: comment.body,
            });
          }
        }
      }
    }

    // Records
    for (const record of project.records ?? []) {
      const isAssigned = (record.assignees ?? []).includes(name) || (record.assignees ?? []).includes(resourceId);
      const closed =
        record.status === "closed" ||
        record.status === "accepted" ||
        record.status === "superseded" ||
        record.status === "deprecated";
      const closedDate = parseDay(record.closedAt) ?? parseDay(record.updatedAt) ?? parseDay(record.due);

      if (isAssigned) {
        items.push({
          id: record.id,
          projectId: project.id,
          projectName: project.name,
          title: record.title,
          kind: "record",
          subKind: record.kind,
          done: closed,
          date: closedDate,
          dueDate: parseDay(record.due),
          summary: record.sections?.summary ?? record.sections?.statement ?? record.sections?.resolution,
        });

        if (closed && closedDate) {
          const typeBadge =
            record.kind === "risk"
              ? "리스크 조치"
              : record.kind === "issue"
              ? "이슈 해결"
              : record.kind === "policy"
              ? "방침 확정"
              : "의사결정";
          activities.push({
            id: `act-rec-${record.id}`,
            date: closedDate,
            type: record.kind === "risk" ? "risk_mitigated" : record.kind === "policy" ? "policy" : "record_closed",
            badge: typeBadge,
            title: record.title,
            projectName: project.name,
            projectId: project.id,
            detail: record.sections?.resolution ?? record.sections?.statement ?? record.sections?.summary,
          });
        }
      }

      for (const comment of record.comments ?? []) {
        if (comment.author === name && comment.at) {
          const cDate = parseDay(comment.at);
          if (cDate) {
            activities.push({
              id: `act-rcm-${comment.id}`,
              date: cDate,
              type: "comment",
              badge: "검토 의견",
              title: record.title,
              projectName: project.name,
              projectId: project.id,
              detail: comment.body,
            });
          }
        }
      }
    }
  }

  const completedItems = items.filter((it) => it.done);
  const completedTasksCount = completedItems.filter((it) => it.kind === "schedule" || it.kind === "execution").length;

  // 1. BURNDOWN & PACE
  const allDates = items.flatMap((it) => (it.date ? [it.date] : []));
  const earliestDate = allDates.length > 0 ? allDates.reduce((min, d) => (d < min ? d : min), today) : addDays(today, -28);
  const latestDate = allDates.length > 0 ? allDates.reduce((max, d) => (d > max ? d : max), today) : addDays(today, 28);

  const startDate = addDays(weekStartMonday(earliestDate), -7);
  const targetDate = addDays(weekStartMonday(latestDate), 14);

  const totalScope = Math.max(items.length, 1);
  const completedScope = completedItems.length;
  const progressPercent = Math.min(100, Math.round((completedScope / totalScope) * 100));

  const totalDays = Math.max(1, toDay(targetDate) - toDay(startDate));
  const elapsedDays = Math.max(1, toDay(today) - toDay(startDate));
  const elapsedRatio = Math.min(1, Math.max(0.1, elapsedDays / totalDays));

  const actualRatio = completedScope / totalScope;
  let paceRatio = Math.round((actualRatio / elapsedRatio) * 10) / 10;
  if (!Number.isFinite(paceRatio) || paceRatio <= 0) paceRatio = 1.0;

  let forecastDaysFromStart: number;
  if (progressPercent >= 100) {
    forecastDaysFromStart = elapsedDays;
  } else if (completedScope > 0 && elapsedDays > 0) {
    const dailyVelocity = completedScope / elapsedDays;
    const remainingTasks = totalScope - completedScope;
    const remainingDaysNeeded = Math.ceil(remainingTasks / dailyVelocity);
    forecastDaysFromStart = elapsedDays + remainingDaysNeeded;
  } else {
    forecastDaysFromStart = totalDays;
  }

  const forecastDate = addDays(startDate, Math.min(forecastDaysFromStart, totalDays * 2));
  const daysMargin = toDay(forecastDate) - toDay(targetDate);
  const isAhead = daysMargin <= 0;
  const isOverdueRisk = daysMargin > 0 || items.some((it) => !it.done && !!it.date && it.date < today);

  const actualCurve: Array<{ date: string; percent: number }> = [];
  let curDate = startDate;
  let runningCompleted = 0;
  while (curDate <= today) {
    const countOnDay = completedItems.filter((it) => it.date && it.date <= curDate).length;
    runningCompleted = countOnDay;
    const pct = Math.min(progressPercent, Math.round((runningCompleted / totalScope) * 100));
    actualCurve.push({ date: curDate, percent: pct });
    curDate = addDays(curDate, 7);
  }
  if (!actualCurve.some((pt) => pt.date === today)) {
    actualCurve.push({ date: today, percent: progressPercent });
  }

  const thisWeekStart = weekStartMonday(today);
  const weeklyThroughput: MemberWeeklyThroughput[] = [];
  const targetAllowancePerWeek = Math.max(1.5, Math.round((totalScope / (totalDays / 7)) * 10) / 10);

  for (let offset = -5; offset <= 0; offset += 1) {
    const wStart = addDays(thisWeekStart, offset * 7);
    const wEnd = addDays(wStart, 6);
    const count = completedItems.filter((it) => it.date && it.date >= wStart && it.date <= wEnd).length;
    const isCurrent = offset === 0;
    const shortLabel = formatDate(wStart, "short");
    weeklyThroughput.push({
      weekStart: wStart,
      label: shortLabel,
      count,
      isOverTarget: count >= targetAllowancePerWeek,
      isCurrent,
    });
  }

  const burndown: MemberBurndownForecast = {
    startDate,
    today,
    targetDate,
    totalScope,
    completedScope,
    progressPercent,
    paceRatio,
    forecastDate,
    daysMargin,
    isAhead,
    isOverdueRisk,
    actualCurve,
    weeklyThroughput,
    targetAllowancePerWeek,
  };

  // 2. PROJECT ACTIVITY STRIPS
  const projectStrips: MemberProjectStrip[] = activeProjects
    .filter((p) => {
      const hasSched = p.tasks.some((t) => t.assignedResourceId === resourceId || t.assignee === name);
      const hasExec = (p.executionTasks ?? []).some((t) => (t.assignees ?? []).includes(name));
      const hasRec = (p.records ?? []).some((r) => (r.assignees ?? []).includes(name));
      const hasAssign = (p.resourceAssignments ?? []).some((a) => a.resourceId === resourceId);
      return hasSched || hasExec || hasRec || hasAssign;
    })
    .map((p) => {
      const assignment = (p.resourceAssignments ?? []).find((a) => a.resourceId === resourceId);
      const role = assignment?.projectRole || "인프라 / 개발";
      const allocationRatio = assignment?.allocationRatio ?? 0.8;

      const pItems = items.filter((it) => it.projectId === p.id);
      const pCompleted = pItems.filter((it) => it.done).length;
      const progress = pItems.length > 0 ? Math.round((pCompleted / pItems.length) * 100) : 50;

      const weeks: MemberProjectStrip["weeks"] = [];
      for (let offset = -5; offset <= 2; offset += 1) {
        const wStart = addDays(thisWeekStart, offset * 7);
        const wEnd = addDays(wStart, 6);
        const count = pItems.filter((it) => it.date && it.date >= wStart && it.date <= wEnd).length;
        let level: 0 | 1 | 2 | 3 | 4 = 0;
        if (count >= 4) level = 4;
        else if (count === 3) level = 3;
        else if (count === 2) level = 2;
        else if (count === 1) level = 1;

        weeks.push({
          weekStart: wStart,
          level,
          count,
          isCurrent: offset === 0,
        });
      }

      let statusLabel = "적정 부하";
      let statusTone: MemberProjectStrip["statusTone"] = "success";
      if (allocationRatio > 1.05) {
        statusLabel = "과부하 주의";
        statusTone = "warn";
      } else if (allocationRatio < 0.3) {
        statusLabel = "여유";
        statusTone = "neutral";
      }

      return {
        projectId: p.id,
        projectName: p.name,
        projectRole: role,
        allocationRatio,
        progress,
        paceRatio,
        targetDate: p.statusDate,
        statusLabel,
        statusTone,
        weeks,
      };
    });

  // 3. ACTIVITY FEED
  const activityFeed = activities
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 15);

  // 4. ACTIVITY HEATMAP (Last 6 weeks, Monday to Sunday)
  const HEATMAP_COLUMNS = ["월", "화", "수", "목", "금", "토", "일"].map((label, idx) => ({
    key: `col-${idx}`,
    label,
  }));
  const WEEKDAY_NAMES = ["월요일", "화요일", "수요일", "목요일", "금요일", "토요일", "일요일"];

  const heatmapRows: MemberHeatmapRow[] = [];
  const weekdayTotals = [0, 0, 0, 0, 0, 0, 0];
  let totalHeatmapActivities = 0;

  for (let offset = -5; offset <= 0; offset += 1) {
    const wStart = addDays(thisWeekStart, offset * 7);
    const rowKey = `w-${wStart}`;
    const rowLabel = formatDate(wStart, "short");
    const cells: MemberHeatmapCell[] = [];

    for (let d = 0; d < 7; d += 1) {
      const cellDate = addDays(wStart, d);
      const count = activities.filter((act) => act.date === cellDate).length;
      weekdayTotals[d] = (weekdayTotals[d] ?? 0) + count;
      totalHeatmapActivities += count;

      const isCurrentDay = cellDate === today;
      const tooltip =
        count > 0
          ? `${formatDate(cellDate, "monthDayWeekday")} · 활동 ${count}건`
          : `${formatDate(cellDate, "monthDayWeekday")} · 활동 없음`;

      cells.push({
        key: `${wStart}-${d}`,
        date: cellDate,
        value: count,
        tooltip,
        marked: isCurrentDay,
      });
    }

    heatmapRows.push({
      key: rowKey,
      label: rowLabel,
      cells,
    });
  }

  const peakVal = Math.max(...weekdayTotals);
  const peakColumn = peakVal > 0 ? weekdayTotals.indexOf(peakVal) : null;
  const busiestWeekdayLabel = peakColumn !== null ? WEEKDAY_NAMES[peakColumn] ?? null : null;

  const activityHeatmap: MemberActivityHeatmap = {
    columns: HEATMAP_COLUMNS,
    rows: heatmapRows,
    peakColumn,
    totalActivities: totalHeatmapActivities,
    busiestWeekdayLabel,
  };

  // 5. TASK DISTRIBUTION
  const doneCount = completedTasksCount;
  const overdueItems = items.filter((it) => !it.done && !!it.dueDate && it.dueDate < today);
  const overdueCount = overdueItems.length;
  const inProgressItems = items.filter(
    (it) => !it.done && ((it.percent ?? 0) > 0 || (it.dueDate && it.dueDate >= today && it.date === today)),
  );
  const inProgressCount = inProgressItems.length;
  const totalTasks = items.filter((it) => it.kind === "schedule" || it.kind === "execution").length;
  const notStartedCount = Math.max(0, totalTasks - doneCount - overdueCount - inProgressCount);

  const taskDistribution: MemberTaskDistribution = {
    total: totalTasks,
    done: doneCount,
    inProgress: inProgressCount,
    overdue: overdueCount,
    notStarted: notStartedCount,
  };

  // 6. PROJECT WORKLOAD
  const projectWorkload: MemberProjectWorkloadItem[] = activeProjects
    .filter((p) => {
      const assignment = (p.resourceAssignments ?? []).find((a) => a.resourceId === resourceId);
      const hasTask = p.tasks.some((t) => t.assignedResourceId === resourceId || t.assignee === name);
      const hasExec = (p.executionTasks ?? []).some((t) => (t.assignees ?? []).includes(name));
      return Boolean(assignment || hasTask || hasExec);
    })
    .map((p) => {
      const assignment = (p.resourceAssignments ?? []).find((a) => a.resourceId === resourceId);
      const pItems = items.filter((it) => it.projectId === p.id);
      const pDone = pItems.filter((it) => it.done).length;
      const pOpen = pItems.filter((it) => !it.done).length;
      const plannedMM = pItems.reduce((acc, it) => acc + (it.plannedMM ?? 0), 0);

      return {
        projectId: p.id,
        projectName: p.name,
        role: assignment?.projectRole || "엔지니어",
        allocationRatio: assignment?.allocationRatio ?? (pOpen > 0 ? 0.5 : 0),
        plannedMM: Math.round(plannedMM * 100) / 100,
        openTasksCount: pOpen,
        completedTasksCount: pDone,
      };
    });

  return {
    burndown,
    activityFeed,
    projectStrips,
    activityHeatmap,
    taskDistribution,
    projectWorkload,
  };
}

/**
 * Mock scenario dataset for UI/UX testing
 */
export function getMockMemberScenario(
  scenario: MemberScenario,
  base: {
    analytics: MemberAnalytics;
    load: { remaining: LoadMark[]; remainingMM: number; nextDue?: string };
    plans: PersonWork["activePlans"];
    ratio: number;
  },
  today: ISODate,
): {
  analytics: MemberAnalytics;
  remaining: LoadMark[];
  plans: PersonWork["activePlans"];
  ratio: number;
  remainingMM: number;
  nextDue?: string;
} {
  if (scenario === "default") {
    return {
      analytics: base.analytics,
      remaining: base.load.remaining,
      plans: base.plans,
      ratio: base.ratio,
      remainingMM: base.load.remainingMM,
      nextDue: base.load.nextDue,
    };
  }

  if (scenario === "empty") {
    const emptyThroughput: MemberWeeklyThroughput[] = base.analytics.burndown.weeklyThroughput.map((w) => ({
      ...w,
      count: 0,
      isOverTarget: false,
    }));

    return {
      analytics: {
        burndown: {
          ...base.analytics.burndown,
          totalScope: 0,
          completedScope: 0,
          progressPercent: 0,
          paceRatio: 1.0,
          daysMargin: 0,
          isAhead: true,
          isOverdueRisk: false,
          actualCurve: [{ date: today, percent: 0 }],
          weeklyThroughput: emptyThroughput,
          targetAllowancePerWeek: 0,
        },
        activityFeed: [],
        projectStrips: [],
        activityHeatmap: {
          columns: base.analytics.activityHeatmap.columns,
          rows: base.analytics.activityHeatmap.rows.map((r) => ({
            ...r,
            cells: r.cells.map((c) => ({ ...c, value: 0, tooltip: `${formatDate(c.date, "monthDayWeekday")} · 활동 없음` })),
          })),
          peakColumn: null,
          totalActivities: 0,
          busiestWeekdayLabel: null,
        },
        taskDistribution: {
          total: 0,
          done: 0,
          inProgress: 0,
          overdue: 0,
          notStarted: 0,
        },
        projectWorkload: [],
      },
      remaining: [],
      plans: [],
      ratio: 0,
      remainingMM: 0,
      nextDue: undefined,
    };
  }

  if (scenario === "heavy") {
    const heavyFeed: MemberActivityItem[] = [
      { id: "af1", date: today, type: "comment", badge: "검토 의견", title: "개발 서버 방화벽 오픈 대기", projectName: "ERP 시스템 고도화", projectId: "p1", detail: "승인 티켓 최종 승인 처리되어 환경 오픈 완료했습니다." },
      { id: "af2", date: addDays(today, -1), type: "task_done", badge: "작업 완료", title: "CI/CD 헬름 차트 배포 구성", projectName: "ERP 시스템 고도화", projectId: "p1", detail: "개발 및 스테이징 환경 배포 파이프라인 검증 완료" },
      { id: "af3", date: addDays(today, -2), type: "policy", badge: "방침 확정", title: "접속·권한 변경 로그 보관 정책 (3년)", projectName: "ERP 시스템 고도화", projectId: "p1", detail: "고객사 감사팀과 협의하여 3년 보관 방침 확정" },
      { id: "af4", date: addDays(today, -4), type: "task_done", badge: "작업 완료", title: "DB 연결 풀(HikariCP) 튜닝", projectName: "ERP 시스템 고도화", projectId: "p1", detail: "최대 커넥션 50개 기준 안정성 부하 테스트 완료" },
      { id: "af5", date: addDays(today, -6), type: "risk_mitigated", badge: "리스크 조치", title: "DB 라이선스 도입 지연", projectName: "ERP 시스템 고도화", projectId: "p1", detail: "정식 라이선스 입고 전 임시 라이선스로 개발 차질 방지" },
      { id: "af6", date: addDays(today, -9), type: "record_closed", badge: "이슈 해결", title: "개발 서버 방화벽 미개방 원격 불가", projectName: "ERP 시스템 고도화", projectId: "p1", detail: "포트 22, 8080 신청서 승인 및 개방 완료" },
      { id: "af7", date: addDays(today, -12), type: "task_done", badge: "일정 완료", title: "개발 환경 구축", projectName: "ERP 시스템 고도화", projectId: "p1", detail: "공수 0.4 M/M 달성" },
      { id: "af8", date: addDays(today, -15), type: "task_done", badge: "작업 완료", title: "로봇 GPU 제어 컨테이너 이미지 패키징", projectName: "지능형 로봇 제어 시스템", projectId: "p2", detail: "CUDA 12.2 베이스 런타임 이미지 빌드" },
      { id: "af9", date: addDays(today, -18), type: "comment", badge: "검토 의견", title: "MES 협력사 인터페이스 사양", projectName: "ERP 시스템 고도화", projectId: "p1", detail: "어댑터 계층 분리하여 연말 업그레이드 영향 최소화 제안" },
      { id: "af10", date: addDays(today, -22), type: "policy", badge: "방침 확정", title: "협력사 계정 발급 및 만료 정책", projectName: "ERP 시스템 고도화", projectId: "p1", detail: "계약 종료일 자동 만료 규칙 수립" },
    ];

    const heavyRemaining: LoadMark[] = [
      { id: "hr1", projectId: "p1", projectName: "ERP 시스템 고도화", title: "운영 서버 SSL 인증서 갱신 및 배포", kind: "execution", done: false, overdue: false, date: addDays(today, 3), status: "inProgress" },
      { id: "hr2", projectId: "p1", projectName: "ERP 시스템 고도화", title: "성능 부하 테스트 스크립트 작성", kind: "schedule", done: false, overdue: false, date: addDays(today, 6), percent: 30, plannedMM: 0.2 },
      { id: "hr3", projectId: "p2", projectName: "지능형 로봇 제어 시스템", title: "ROS2 노드 간 latency 벤치마크", kind: "execution", done: false, overdue: false, date: addDays(today, 10), status: "notStarted" },
      { id: "hr4", projectId: "p1", projectName: "ERP 시스템 고도화", title: "재해 복구 리허설 이관 점검", kind: "execution", done: false, overdue: false, date: addDays(today, 14), status: "notStarted" },
      { id: "hr5", projectId: "p1", projectName: "ERP 시스템 고도화", title: "인프라 보안 감사 체크리스트 작성", kind: "execution", done: false, overdue: false, date: addDays(today, 18), status: "notStarted" },
      { id: "hr6", projectId: "p2", projectName: "지능형 로봇 제어 시스템", title: "임베디드 리눅스 커널 패치 적용", kind: "execution", done: false, overdue: false, date: addDays(today, 22), status: "notStarted" },
    ];

    const heavyPlans: PersonWork["activePlans"] = [
      { id: "p1", name: "ERP 시스템 고도화", role: "클라우드 / DevOps 엔지니어" },
      { id: "p2", name: "지능형 로봇 제어 시스템", role: "인프라 엔지니어" },
      { id: "p3", name: "차세대 빅데이터 분석 플랫폼", role: "클러스터 아키텍트" },
      { id: "p4", name: "사내 보안 인프라 표준화", role: "보안 자문" },
    ];

    const heavyThroughput: MemberWeeklyThroughput[] = [
      { weekStart: addDays(today, -35), label: "09/17", count: 6, isOverTarget: true, isCurrent: false },
      { weekStart: addDays(today, -28), label: "09/24", count: 5, isOverTarget: true, isCurrent: false },
      { weekStart: addDays(today, -21), label: "10/01", count: 4, isOverTarget: true, isCurrent: false },
      { weekStart: addDays(today, -14), label: "10/08", count: 7, isOverTarget: true, isCurrent: false },
      { weekStart: addDays(today, -7), label: "10/15", count: 8, isOverTarget: true, isCurrent: false },
      { weekStart: today, label: "이번 주", count: 5, isOverTarget: true, isCurrent: true },
    ];

    return {
      analytics: {
        burndown: {
          ...base.analytics.burndown,
          totalScope: 38,
          completedScope: 28,
          progressPercent: 74,
          paceRatio: 1.3,
          forecastDate: addDays(today, 14),
          daysMargin: -6,
          isAhead: true,
          isOverdueRisk: false,
          weeklyThroughput: heavyThroughput,
          targetAllowancePerWeek: 4.0,
        },
        activityFeed: heavyFeed,
        projectStrips: [
          {
            projectId: "p1",
            projectName: "ERP 시스템 고도화",
            projectRole: "클라우드 / DevOps",
            allocationRatio: 0.8,
            progress: 80,
            paceRatio: 1.3,
            statusLabel: "적정 부하",
            statusTone: "success",
            weeks: heavyThroughput.map((w, idx) => ({ weekStart: w.weekStart, level: (idx % 2 === 0 ? 3 : 4) as 0|1|2|3|4, count: w.count, isCurrent: w.isCurrent })),
          },
          {
            projectId: "p2",
            projectName: "지능형 로봇 제어 시스템",
            projectRole: "인프라 엔지니어",
            allocationRatio: 0.4,
            progress: 65,
            paceRatio: 1.1,
            statusLabel: "적정 부하",
            statusTone: "success",
            weeks: heavyThroughput.map((w) => ({ weekStart: w.weekStart, level: 2 as 0|1|2|3|4, count: 2, isCurrent: w.isCurrent })),
          },
        ],
        activityHeatmap: {
          columns: base.analytics.activityHeatmap.columns,
          rows: base.analytics.activityHeatmap.rows.map((r, rIdx) => ({
            ...r,
            cells: r.cells.map((c, cIdx) => {
              const count = (rIdx * 2 + cIdx) % 5;
              return {
                ...c,
                value: count,
                tooltip: count > 0 ? `${formatDate(c.date, "monthDayWeekday")} · 활동 ${count}건` : `${formatDate(c.date, "monthDayWeekday")} · 활동 없음`,
              };
            }),
          })),
          peakColumn: 2,
          totalActivities: 52,
          busiestWeekdayLabel: "수요일",
        },
        taskDistribution: {
          total: 38,
          done: 22,
          inProgress: 6,
          overdue: 2,
          notStarted: 8,
        },
        projectWorkload: [
          {
            projectId: "p1",
            projectName: "ERP 시스템 고도화",
            role: "클라우드 / DevOps",
            allocationRatio: 0.8,
            plannedMM: 3.2,
            openTasksCount: 4,
            completedTasksCount: 16,
          },
          {
            projectId: "p2",
            projectName: "지능형 로봇 제어 시스템",
            role: "인프라 엔지니어",
            allocationRatio: 0.4,
            plannedMM: 1.6,
            openTasksCount: 2,
            completedTasksCount: 6,
          },
        ],
      },
      remaining: heavyRemaining,
      plans: heavyPlans,
      ratio: 1.2,
      remainingMM: 0.8,
      nextDue: addDays(today, 3),
    };
  }

  const unbalFeed: MemberActivityItem[] = [
    { id: "uaf1", date: today, type: "comment", badge: "검토 의견", title: "인프라 상태 점검", projectName: "ERP 시스템 고도화", projectId: "p1", detail: "서버 리소스 정상 범위 유지 중입니다." },
  ];

  const unbalRemaining: LoadMark[] = [
    { id: "ur1", projectId: "p1", projectName: "ERP 시스템 고도화", title: "분기 보안 패치 적용", kind: "execution", done: false, overdue: false, date: addDays(today, 5), status: "inProgress" },
  ];

  const unbalPlans: PersonWork["activePlans"] = [
    { id: "p1", name: "ERP 시스템 고도화", role: "클라우드 엔지니어" },
  ];

  return {
    analytics: {
      burndown: base.analytics.burndown,
      activityFeed: unbalFeed,
      projectStrips: base.analytics.projectStrips,
      activityHeatmap: {
        columns: base.analytics.activityHeatmap.columns,
        rows: base.analytics.activityHeatmap.rows.map((r) => ({
          ...r,
          cells: r.cells.map((c, cIdx) => {
            const count = cIdx === 0 || cIdx === 4 ? 2 : 0;
            return {
              ...c,
              value: count,
              tooltip: count > 0 ? `${formatDate(c.date, "monthDayWeekday")} · 활동 ${count}건` : `${formatDate(c.date, "monthDayWeekday")} · 활동 없음`,
            };
          }),
        })),
        peakColumn: 0,
        totalActivities: 12,
        busiestWeekdayLabel: "월요일",
      },
      taskDistribution: {
        total: 11,
        done: 10,
        inProgress: 1,
        overdue: 0,
        notStarted: 0,
      },
      projectWorkload: [
        {
          projectId: "p1",
          projectName: "ERP 시스템 고도화",
          role: "클라우드 엔지니어",
          allocationRatio: 0.8,
          plannedMM: 1.2,
          openTasksCount: 1,
          completedTasksCount: 10,
        },
      ],
    },
    remaining: unbalRemaining,
    plans: unbalPlans,
    ratio: 0.8,
    remainingMM: 0.1,
    nextDue: addDays(today, 5),
  };
}
